import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useSpecimens,
  useScanSpecimen,
  useProcessSpecimen,
  useCompleteSpecimen,
  useLabResults,
} from '../../services/httpServices/queries';
import { diagnosticService } from '../../services/httpServices/diagnosticService';
import { laboratoryService } from '../../services/httpServices/laboratoryService';
import type { LabSpecimen } from '../../services/httpServices/diagnosticService';
import { specimenCategoryNames } from '../../utils/specimen';
import { joinFullName } from '../../utils/patientName';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import { useAppSelector } from '../../redux/store/hooks';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/ui/Pagination';
import { SpecimenDetailsModal } from '../../components/ui/SpecimenDetailsModal';
import { toast } from 'sonner';
import {
  ScanBarcode,
  Loader2,
  AlertCircle,
  Search,
  FilterX,
  FlaskConical,
  User,
  CheckCircle,
  Plus,
  ClipboardCheck,
  Play,
} from 'lucide-react';

const statusMeta: Record<string, { label: string; variant: 'warning' | 'secondary' | 'primary' | 'success' | 'danger' | 'default' }> = {
  PENDING: { label: 'Pending', variant: 'warning' },
  COLLECTED: { label: 'Collected', variant: 'primary' },
  RECEIVED: { label: 'Received', variant: 'secondary' },
  PROCESSING: { label: 'Processing', variant: 'primary' },
  COMPLETED: { label: 'Completed', variant: 'success' },
  REJECTED: { label: 'Rejected', variant: 'danger' },
  CANCELLED: { label: 'Cancelled', variant: 'danger' },
};

const specimenTests = (s: LabSpecimen) =>
  (s.items || []).map((i) => i.receiptItem?.testName).filter(Boolean).join(', ');

function OrderResultEditor({ orderId, testName, onValidated }: { orderId: string; testName: string; onValidated: () => void }) {
  const { user } = useAppSelector((state) => state.auth);
  const { hasPermission } = usePermissions();
  const canEnterResult = hasPermission('diagnostics:result:create');
  const canValidateResult = hasPermission('diagnostics:result:validate');
  const queryClient = useQueryClient();
  const { data: resultsData } = useLabResults(orderId);
  const results: any[] = resultsData?.data || [];
  const [form, setForm] = useState({ testParameter: '', resultValue: '', unit: '', referenceRange: '' });
  const [saving, setSaving] = useState(false);

  const addResult = async () => {
    if (!form.testParameter.trim()) return;
    setSaving(true);
    try {
      await laboratoryService.createResult({ orderId, ...form });
      setForm({ testParameter: '', resultValue: '', unit: '', referenceRange: '' });
      await queryClient.invalidateQueries({ queryKey: ['lab-results', orderId] });
      toast.success('Result saved');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save result');
    } finally {
      setSaving(false);
    }
  };

  const validate = async () => {
    setSaving(true);
    try {
      await laboratoryService.validateResults(orderId, user?.id || '');
      // Completes the specimen if all its tests are validated (cascades routing + revenue).
      await diagnosticService.syncSpecimensForOrder(orderId);
      toast.success(`Validated ${testName}`);
      onValidated();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to validate results');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-background rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-text flex items-center gap-1.5">
          <FlaskConical size={14} className="text-primary" /> {testName}
        </span>
        <span className="text-[10px] font-mono text-text-muted">{orderId.slice(0, 8)}</span>
      </div>

      {results.length > 0 && (
        <div className="text-xs space-y-1">
          {results.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-text-muted">
              <span className="text-text">{r.testParameter}</span>
              <span>=</span>
              <span className="font-medium text-text">{r.resultValue}</span>
              {r.unit && <span>{r.unit}</span>}
              {r.referenceRange && <span className="text-text-muted/70">({r.referenceRange})</span>}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Input
          placeholder="Parameter"
          value={form.testParameter}
          onChange={(e) => setForm((p) => ({ ...p, testParameter: e.target.value }))}
        />
        <Input
          placeholder="Value"
          value={form.resultValue}
          onChange={(e) => setForm((p) => ({ ...p, resultValue: e.target.value }))}
        />
        <Input
          placeholder="Unit"
          value={form.unit}
          onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))}
        />
        <Input
          placeholder="Ref. range"
          value={form.referenceRange}
          onChange={(e) => setForm((p) => ({ ...p, referenceRange: e.target.value }))}
        />
      </div>

      <div className="flex items-center gap-2">
        {canEnterResult && (
          <Button size="sm" variant="outline" onClick={addResult} disabled={saving || !form.testParameter.trim()}>
            <Plus size={12} className="mr-1" /> Add Result
          </Button>
        )}
        {canValidateResult && (
          <Button size="sm" onClick={validate} disabled={saving}>
            {saving ? <Loader2 size={12} className="mr-1 animate-spin" /> : <ClipboardCheck size={12} className="mr-1" />}
            Validate & Complete
          </Button>
        )}
      </div>
    </div>
  );
}

export function LabWorkbench() {
  const { hasPermission } = usePermissions();
  const canProcess = hasPermission('diagnostics:specimen:process');
  const canComplete = hasPermission('diagnostics:specimen:complete');

  const [tubeBarcode, setTubeBarcode] = useState('');
  const [scanned, setScanned] = useState<LabSpecimen | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [queueStatus, setQueueStatus] = useState('RECEIVED');
  const [page, setPage] = useState(1);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const limit = 20;

  const filters = useMemo(
    () => ({ status: queueStatus || undefined, search: debouncedSearch || undefined }),
    [queueStatus, debouncedSearch],
  );
  const { data, isLoading, error, refetch } = useSpecimens(filters, page, limit);
  const queue = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const scanSpecimen = useScanSpecimen();
  const processSpecimen = useProcessSpecimen();
  const completeSpecimen = useCompleteSpecimen();

  const refresh = (updated: LabSpecimen) => setScanned((prev) => (prev && prev.id === updated.id ? updated : prev));

  const handleScan = async () => {
    if (!tubeBarcode.trim()) return;
    try {
      const res = await scanSpecimen.mutateAsync(tubeBarcode.trim());
      setScanned(res.data);
      setTubeBarcode('');
    } catch (err: any) {
      toast.error(err?.message || 'Specimen not found for this barcode');
    }
  };

  const openSpecimen = (s: LabSpecimen) => setScanned(s);

  const handleProcess = async () => {
    if (!scanned) return;
    const res = await processSpecimen.mutateAsync(scanned.id);
    toast.success('Specimen processing');
    refresh(res.data);
  };

  const handleComplete = async () => {
    if (!scanned) return;
    const res = await completeSpecimen.mutateAsync(scanned.id);
    toast.success('Specimen completed');
    refresh(res.data);
    refetch();
  };

  const orderItems = (scanned?.items || []).filter((i) => i.labOrderId);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <FlaskConical className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-text">Lab Bench</h1>
          <p className="text-xs text-text-muted">Scan received tubes, enter results, and validate to complete the specimen.</p>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
        <label className="block text-xs font-medium text-text-muted">Scan Tube Barcode</label>
        <div className="flex gap-2">
          <Input
            placeholder="Scan or type specimen barcode..."
            value={tubeBarcode}
            onChange={(e) => setTubeBarcode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleScan()}
          />
          <Button onClick={handleScan} disabled={scanSpecimen.isPending || !tubeBarcode.trim()}>
            {scanSpecimen.isPending ? <Loader2 size={14} className="animate-spin" /> : <ScanBarcode size={14} />}
          </Button>
        </div>
      </div>

      {scanned && (
        <div className="bg-surface border border-primary/30 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <User size={16} className="text-primary" />
              <span className="text-sm font-semibold text-text">
                {joinFullName(scanned.patient || {})}
              </span>
              <Badge variant={statusMeta[scanned.status]?.variant || 'default'}>
                {statusMeta[scanned.status]?.label || scanned.status}
              </Badge>
            </div>
            <span className="text-[11px] font-mono text-text-muted">{scanned.specimenBarcode}</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-text-muted">Container</p>
              <p className="text-text font-medium">
                {scanned.specimenContainerCode || 'N/A'}
                {scanned.containerColor ? ` · ${scanned.containerColor}` : ''}
              </p>
            </div>
            <div>
              <p className="text-text-muted">Category</p>
              <p className="text-text font-medium">{specimenCategoryNames(scanned) || 'N/A'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-text-muted">Tests</p>
              <p className="text-text font-medium">{specimenTests(scanned) || 'N/A'}</p>
            </div>
          </div>

          {(scanned.status === 'RECEIVED' || scanned.status === 'PROCESSING') && (canProcess || canComplete) && (
            <div className="flex items-center gap-2 pt-2 border-t border-border/60">
              {scanned.status === 'RECEIVED' && canProcess && (
                <Button size="sm" variant="outline" onClick={handleProcess} disabled={processSpecimen.isPending}>
                  <Play size={12} className="mr-1" /> Start Processing
                </Button>
              )}
              {canComplete && (
                <Button size="sm" onClick={handleComplete} disabled={completeSpecimen.isPending}>
                  <CheckCircle size={12} className="mr-1" /> Complete Specimen
                </Button>
              )}
            </div>
          )}

          <div className="space-y-2 pt-2 border-t border-border/60">
            <p className="text-xs font-semibold text-text">Results</p>
            {orderItems.length === 0 ? (
              <p className="text-xs text-text-muted">No linked lab orders for this specimen.</p>
            ) : (
              orderItems.map((item) => (
                <OrderResultEditor
                  key={item.id}
                  orderId={item.labOrderId as string}
                  testName={item.receiptItem?.testName || 'Test'}
                  onValidated={async () => {
                    await refetch();
                    try {
                      const res = await scanSpecimen.mutateAsync(scanned.specimenBarcode);
                      setScanned(res.data);
                    } catch {
                      /* keep current */
                    }
                  }}
                />
              ))
            )}
          </div>
        </div>
      )}

      <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              placeholder="Search bench queue..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <select
            value={queueStatus}
            onChange={(e) => setQueueStatus(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-text text-sm"
          >
            <option value="">All Status</option>
            <option value="RECEIVED">Received</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETED">Completed</option>
          </select>
          {search && (
            <Button variant="ghost" size="sm" onClick={() => setSearch('')}>
              <FilterX size={14} className="mr-1" /> Clear
            </Button>
          )}
        </div>

        {error ? (
          <div className="flex items-center gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
            <AlertCircle size={16} /> Failed to load the bench queue
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : queue.length === 0 ? (
          <p className="text-sm text-text-muted py-8 text-center">No specimens in the bench queue.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Specimen</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Patient</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Category</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Tests</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {queue.map((s) => (
                  <tr key={s.id} className="hover:bg-background/40 transition-colors">
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setDetailsId(s.id)}
                        className="text-xs font-mono text-primary hover:underline"
                        title="View specimen details"
                      >
                        {s.specimenBarcode}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-sm text-text">
                      {joinFullName(s.patient || {})}
                    </td>
                    <td className="px-3 py-2 text-sm text-text-muted">{specimenCategoryNames(s) || '—'}</td>
                    <td className="px-3 py-2 text-xs text-text-muted">{specimenTests(s)}</td>
                    <td className="px-3 py-2">
                      <Badge variant={statusMeta[s.status]?.variant || 'default'}>{statusMeta[s.status]?.label || s.status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => openSpecimen(s)}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="specimens"
        />
      </div>

      <SpecimenDetailsModal specimenId={detailsId} onClose={() => setDetailsId(null)} />
    </div>
  );
}